import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Lock, Phone, Upload } from 'lucide-react'
import { referralRequestsAPI } from '../../services/api'
import { useCaseStore } from '../../stores'
import { formatUsPhone } from '../../utils/caseReport'
import { withdrawCaseFromDoctor } from '../../utils/withdrawCase'
import { referralCardState } from '../../utils/referralCard'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

/**
 * ReferralRequestCard — s106, canvas "Orella Referrals Screens" A5 (waiting), A6 (no referral after 3
 * business days) and A8 (on file), on Case Home. Copy approved 2026-10-16. Reads the case's referral
 * requests (GET /cases/<id>/referral-requests) and shows the newest one that is waiting or on file;
 * nothing at all when there is none (utils/referralCard decides).
 *   ★ Overdue = now past the request's own due_at (the API's _ref_due_at: 5 PM PT, 3rd business day).
 *   ★ Cancel (his ruling 2026-10-16, "as the mockup says"): withdraws the request AND takes the case
 *     back from the specialist (utils/withdrawCase, the one implementation), so it can go elsewhere.
 */
const NAVY = '#15354F'
const MUTED = '#4E6577'

const whenOf = (iso, opts) => {
  try { return new Date(iso).toLocaleDateString(undefined, opts) } catch (_) { return '' }
}

export default function ReferralRequestCard({ caseId, doctorName, t }) {
  const navigate = useNavigate()
  const { currentCase, updateAndSaveCase } = useCaseStore()
  const [requests, setRequests] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!caseId) return undefined
    let alive = true
    referralRequestsAPI.listForCase(caseId)
      .then((r) => { if (alive) setRequests(Array.isArray(r?.requests) ? r.requests : []) })
      .catch(() => { if (alive) setRequests([]) })
    return () => { alive = false }
  }, [caseId])

  const view = referralCardState(requests, Date.now())
  if (!view) return null
  const { request: rq, state } = view
  const pcp = rq.pcp && typeof rq.pcp === 'object' ? rq.pcp : {}
  const pcpName = pcp.name || ''
  const phone = pcp.phone ? (formatUsPhone(pcp.phone) || pcp.phone) : ''
  const fields = rq.fields && typeof rq.fields === 'object' ? rq.fields : {}
  const dot = (kind) => ({
    done: { background: '#15803D', color: '#fff' }, now: { background: '#46A8F9', color: '#0B2236' }, wait: { background: '#DCE6EE', color: MUTED },
  }[kind])
  const Step = ({ kind, n, children }) => (
    <div className="flex gap-2.5 text-[13.5px] leading-snug" style={{ color: NAVY }}>
      <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[12px] font-extrabold" style={dot(kind)}>
        {kind === 'done' ? <Check size={13} /> : n}
      </span>
      <span className="min-w-0">{children}</span>
    </div>
  )

  const cancel = async () => {
    if (busy) return
    setBusy(true)
    try {
      await referralRequestsAPI.cancel(rq.id)
      if (currentCase && String(currentCase.id) === String(caseId)) await withdrawCaseFromDoctor(caseId, updateAndSaveCase, currentCase)
      navigate('/case/share', { state: { findDoctor: true } })
    } catch (_) {
      setBusy(false)
    }
  }

  if (state === 'on_file') {
    return (
      <div className="rounded-2xl bg-white p-3.5 flex flex-col gap-2 mb-2" style={{ border: '1px solid #DCE6EE' }} data-testid="referral-card" data-state="on_file">
        <p className="m-0 text-[15px] font-extrabold" style={{ color: NAVY }}>{t('referral.cardTitle', { defaultValue: 'Referral to {{doctor}}', doctor: doctorName })}</p>
        {pcpName && <Step kind="done">{t('referral.stepRequested', { defaultValue: 'Requested from {{pcp}} · {{when}}', pcp: pcpName, when: whenOf(rq.requested_at, { month: 'short', day: 'numeric' }) })}</Step>}
        <Step kind="done">{fields.authNumber
          ? t('referral.stepOnFileDetail', { defaultValue: 'On file · auth #{{auth}} · {{visits}} visits until {{until}}', auth: fields.authNumber, visits: fields.visits || '-', until: fields.validTo || '-' })
          : t('referral.stepOnFile', 'Referral on file')}</Step>
        <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#E3F6E8', color: '#166534' }}>{t('referral.stepOnFile', 'Referral on file')}</span>
        <p className="m-0 text-[12.5px] leading-snug" style={{ color: MUTED }}>{t('referral.checkinNote', 'At check-in the referral shows as your authorization, ready to confirm with one tap.')}</p>
      </div>
    )
  }

  const overdue = state === 'overdue'
  return (
    <div className="rounded-2xl p-3.5 flex flex-col gap-2 mb-2" data-testid="referral-card" data-state={state}
         style={overdue ? { background: '#FFFBEB', border: '1px solid #F59E0B' } : { background: '#fff', border: '1px solid #DCE6EE' }}>
      {overdue ? (
        <>
          <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#FEF3C7', color: '#92400E' }}>{t('referral.overdueChip', 'No referral yet · 3 business days')}</span>
          <p className="m-0 text-[15px] font-extrabold" style={{ color: NAVY }}>{t('referral.overdueTitle', { defaultValue: "Please contact {{pcp}}'s office", pcp: pcpName })}</p>
          <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>{t('referral.overdueBody', { defaultValue: "We asked for your referral on {{when}} and haven't received it. A quick call usually sorts it out.", when: whenOf(rq.requested_at, { weekday: 'short', month: 'short', day: 'numeric' }) })}</p>
        </>
      ) : (
        <>
          <p className="m-0 text-[15px] font-extrabold" style={{ color: NAVY }}>{t('referral.cardTitle', { defaultValue: 'Referral to {{doctor}}', doctor: doctorName })}</p>
          <Step kind="done">{t('referral.stepRequested', { defaultValue: 'Requested from {{pcp}} · {{when}}', pcp: pcpName, when: whenOf(rq.requested_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) })}</Step>
          <Step kind="now" n={2}>
            {t('referral.stepWaiting', { defaultValue: "Waiting for {{pcp}}'s office", pcp: pcpName })}
            <span className="block text-[12.5px]" style={{ color: MUTED }}>{t('referral.stepWaitingNote', { defaultValue: "If it hasn't arrived by {{due}}, we'll let you know to contact the office.", due: whenOf(rq.due_at, { weekday: 'short', month: 'short', day: 'numeric' }) })}</span>
          </Step>
          <Step kind="wait" n={3}>{t('referral.stepOnFile', 'Referral on file')}</Step>
        </>
      )}
      {phone && (
        <a href={`tel:${String(pcp.phone).replace(/[^0-9+]/g, '')}`} data-cta="referral-call-office"
           className="w-full py-2.5 rounded-[14px] text-[14px] font-extrabold text-white flex items-center justify-center gap-2" style={{ background: NAVY }}>
          <Phone size={15} /> {t('referral.callOffice', { defaultValue: "Call {{pcp}}'s office · {{phone}}", pcp: pcpName, phone })}
        </a>
      )}
      <button type="button" onClick={() => navigate('/case/referral-upload', { state: { doctorName } })} data-cta="referral-upload-open"
              className="w-full py-2.5 rounded-[14px] text-[14px] font-bold bg-white flex items-center justify-center gap-2" style={{ color: NAVY, border: '1px solid #C9D6E2' }}>
        <Upload size={15} /> {t('referral.uploadCta', 'I have the referral · upload it')}
      </button>
      {!overdue && (
        <div className="flex gap-2.5 items-center rounded-2xl p-3 text-[13px]" style={{ background: '#EEF2F6', border: '1px dashed #B7C7D6', color: MUTED }}>
          <Lock size={18} className="flex-none" />
          <span><b style={{ color: NAVY }}>{t('referral.bookLocked', { defaultValue: 'Book with {{doctor}}', doctor: doctorName })}</b><br />{t('referral.bookLockedNote', 'Opens once the referral is on file')}</span>
        </div>
      )}
      {overdue && (
        <>
          <button type="button" onClick={cancel} disabled={busy} data-cta="referral-cancel"
                  className="w-full py-2.5 rounded-[14px] text-[14px] font-bold bg-white flex items-center justify-center gap-2" style={{ color: NAVY, border: '1px solid #C9D6E2' }}>
            {busy && <OrellaSpinner size={16} tone="inherit" />}{t('referral.cancelCta', 'Cancel the request · send my case to another doctor')}
          </button>
          <p className="m-0 text-[12.5px] leading-snug" style={{ color: MUTED }}>{t('referral.cancelNote', { defaultValue: "Cancelling withdraws the request at {{pcp}}'s office and takes your case back from {{doctor}}.", pcp: pcpName, doctor: doctorName })}</p>
        </>
      )}
    </div>
  )
}
